import { AsyncThunkResult } from '..';
import {
	LocalizationService,
	SettingsService,
	TranslationsService,
} from '@inter-ikea-kompis/services';
import {
	DexfSettingsEnvironmentEnum,
	DexfSettingsFilterSegmentEnum,
} from '@inter-ikea-kompis/enums/';
import { localeService } from '../../services';
import { fetchConfigThunk } from '../application/thunks';
import {
	fetchLocalizationPending,
	fetchLocalizationSuccess,
	fetchLocalizationFailure,
	setWriteDirection,
	setKompisTranslations,
} from './actions';
import { ApplicationUtils } from '../../util/ApplicationUtils';
import { getDexfApiKey } from '../../util/DexfUtils';
import { Localization, WriteDirection } from 'sofa-reborn-react';
import { setKompisSettings } from '../settings';
import { IDexfSettings } from '@inter-ikea-kompis/types';

export function fetchLocalizationThunk(): AsyncThunkResult {
	return async (dispatch, getState): Promise<void> => {
		dispatch(fetchLocalizationPending());
		const settings = getState().settings.settings;
		const isProduction = ApplicationUtils.isProduction();
		const localizationService = new LocalizationService({
			locale: settings.locale,
			localizationPrefixApplication: settings.localizationPrefixApplication,
			localizationEndpoints: settings.localizationEndpoints,
		});
		const translationService = new TranslationsService({
			locale: settings.locale,
			translationPrefixApplication: settings.localizationPrefixApplication,
			translationEndpoints: [
				'/addon-app/translations/kompis/en-XZ.json',
				'/addon-app/translations/kompis/{language}-{country}.json',
			],
		});

		try {
			const settingsService = new SettingsService({
				dexfApiKey: getDexfApiKey(settings.localizationPrefixApplication, isProduction),
				dexfEnvironment: isProduction
					? DexfSettingsEnvironmentEnum.production
					: DexfSettingsEnvironmentEnum.development,
				dexfApplicationId: settings.localizationPrefixApplication,
				locale: settings.locale,
			});
			const kompisSettingsService = new SettingsService({
				dexfApiKey: '829e294e-1e25-4f81-975e-ad53ac4a3682',
				dexfEnvironment: DexfSettingsEnvironmentEnum.production,
				dexfApplicationId: 'kompis',
				locale: settings.locale,
			});

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const configSettings: any = await settingsService.getApplicationSettings(
				DexfSettingsFilterSegmentEnum.production
			);
			const kompisSettings: IDexfSettings = await kompisSettingsService.getApplicationSettings(
				DexfSettingsFilterSegmentEnum.production
			);

			const data = await localizationService.getLocalizedInformation();
			const kompisTranslations = await translationService.getTranslations();
			if (!data || !configSettings || !kompisTranslations) {
				// eslint-disable-next-line no-console
				console.error('Localization data missing');
				dispatch(fetchLocalizationFailure());
			}
			const writeDirection: WriteDirection =
				configSettings.localisation.writeDirection === 'rtl' ? 'rtl' : 'ltr';
			const useMetricMeasures =
				configSettings.localisation.useMetricMeasures !== undefined
					? configSettings.localisation.useMetricMeasures
					: true;
			/* Add writeDirection to body */
			document.getElementsByTagName('body')[0].setAttribute('dir', writeDirection);

			const localization: Localization = Object.assign(
				{},
				configSettings.localisation,
				{
					writeDirection,
					useMetricMeasures,
				} as Localization,
				configSettings.kompis
			);

			localeService.setTranslations(
				data['translations'],
				{ ...configSettings.appsettings, ...configSettings.gallery },
				localization,
				configSettings.kompis['urls']
			);

			dispatch(setKompisTranslations(kompisTranslations));
			dispatch(setKompisSettings(kompisSettings));
			dispatch(fetchLocalizationSuccess(localization, data.translations));
			/* TODO: Move the fetchConfigThunk to a better place. This is needed here now because otherwise iOS devices crash */
			await dispatch(fetchConfigThunk());
			dispatch(setWriteDirection(writeDirection));
		} catch (_error) {
			const errorMessage =
				'The application can’t be loaded since the following translations are missing: \n\n' +
				'Translations \n\n' +
				'Please contact your national web team to have the translations completed.';

			setTimeout(function () {
				alert(errorMessage);
			}, 1);
		}
	};
}
